// Форматы CRM заявок: даты ДД.ММ.ГГГГ, сроки задач словами, телефоны и ссылки
// для связи. Чистые функции — их же использует доска и карточка.
import type { CrmFileCategory, CrmLinks } from "@osmix/contracts"
import { formatDateish } from "@osmix/formatters"
import type { ApplicationStatus } from "@/api"

const DAY = 86_400_000

/** 04.10.2026 */
export const formatDay = (iso: string | null | undefined) => formatDateish(iso, { dateStyle: "short" })

/** 04.10.2026, 14:30 */
export const formatDayTime = (iso: string | null | undefined) =>
  formatDateish(iso, { dateStyle: "short", timeStyle: "short" })

const time = (date: Date) => date.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" })

const dayStart = (date: Date) => {
  const copy = new Date(date)
  copy.setHours(0, 0, 0, 0)
  return copy.getTime()
}

/** Срок словами: «сегодня, 15:00», «завтра, 11:00», «вчера, 12:00», иначе дата и время. */
export const formatDue = (iso: string | null | undefined): string => {
  if (!iso) return "без срока"
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return "без срока"
  const days = Math.round((dayStart(date) - dayStart(new Date())) / DAY)
  if (days === 0) return `сегодня, ${time(date)}`
  if (days === 1) return `завтра, ${time(date)}`
  if (days === -1) return `вчера, ${time(date)}`
  return formatDayTime(iso)
}

export const plural = (n: number, one: string, few: string, many: string) => {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return one
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few
  return many
}

/** «просрочено на 2 дня» / «на 3 часа» — насколько, а не только что. */
export const overdueBy = (iso: string | null | undefined): string => {
  if (!iso) return ""
  const ms = Date.now() - Date.parse(iso)
  if (ms <= 0) return ""
  const days = Math.floor(ms / DAY)
  if (days >= 1) return `просрочено на ${days} ${plural(days, "день", "дня", "дней")}`
  const hours = Math.max(1, Math.floor(ms / 3_600_000))
  return `просрочено на ${hours} ${plural(hours, "час", "часа", "часов")}`
}

/** Цифры номера для wa.me и tel: 8XXXXXXXXXX и 10 цифр → 7XXXXXXXXXX. */
export const phoneDigits = (phone: string | null | undefined): string => {
  const digits = (phone ?? "").replace(/\D/g, "")
  if (digits.length === 11 && digits.startsWith("8")) return `7${digits.slice(1)}`
  if (digits.length === 10) return `7${digits}`
  return digits
}

/** +7 912 600-33-71 для российских номеров, остальные — как ввели. */
export const formatPhone = (phone: string | null | undefined): string => {
  const digits = phoneDigits(phone)
  if (digits.length === 11 && digits.startsWith("7")) {
    return `+7 ${digits.slice(1, 4)} ${digits.slice(4, 7)}-${digits.slice(7, 9)}-${digits.slice(9, 11)}`
  }
  return (phone ?? "").trim()
}

export const telHref = (phone: string | null | undefined) => {
  const digits = phoneDigits(phone)
  return digits.length >= 10 ? `tel:+${digits}` : null
}

export const whatsappHref = (phone: string | null | undefined) => {
  const digits = phoneDigits(phone)
  return digits.length >= 10 ? `https://wa.me/${digits}` : null
}

/** Telegram: ник контакта, если он есть; иначе по номеру (t.me/+7…). */
export const telegramHref = (handle: string | null | undefined, phone?: string | null) => {
  const clean = (handle ?? "").trim().replace(/^https?:\/\/(www\.)?t(elegram)?\.me\//i, "").replace(/^@/, "")
  if (clean) return `https://t.me/${clean}`
  const digits = phoneDigits(phone)
  return digits.length >= 10 ? `https://t.me/+${digits}` : null
}

export const mailHref = (email: string | null | undefined) => {
  const clean = (email ?? "").trim()
  return clean.includes("@") ? `mailto:${clean}` : null
}

export const LINK_FIELDS: { key: keyof CrmLinks; label: string; base: string }[] = [
  { key: "website", label: "Сайт", base: "" },
  { key: "instagram", label: "Instagram", base: "https://instagram.com/" },
  { key: "vk", label: "ВКонтакте", base: "https://vk.com/" },
  { key: "telegram", label: "Telegram", base: "https://t.me/" },
  { key: "wildberries", label: "Wildberries", base: "" },
  { key: "ozon", label: "Ozon", base: "" },
  { key: "yandexMarket", label: "Яндекс Маркет", base: "" },
  { key: "avito", label: "Avito", base: "" },
]

/** Ссылка из того, что вписал менеджер: полный адрес, домен без протокола или @ник. */
export const linkHref = (key: keyof CrmLinks, value: string | undefined): string | null => {
  const raw = (value ?? "").trim()
  if (!raw) return null
  if (/^https?:\/\//i.test(raw)) return raw
  const base = LINK_FIELDS.find((field) => field.key === key)?.base ?? ""
  const handle = raw.replace(/^@/, "")
  if (base && !handle.includes(".") && !handle.includes("/")) return `${base}${handle}`
  return `https://${handle}`
}

/** Короткая подпись ссылки: без протокола и www. */
export const linkLabel = (value: string) =>
  value
    .trim()
    .replace(/^https?:\/\//i, "")
    .replace(/^www\./i, "")
    .replace(/\/$/, "")

export const fileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} Б`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} КБ`
  return `${(bytes / 1024 / 1024).toLocaleString("ru-RU", { maximumFractionDigits: 1 })} МБ`
}

/** Раздел файла по типу и имени — подсказка при загрузке, менеджер может сменить. */
export const guessFileCategory = (file: { name: string; type: string }): CrmFileCategory => {
  const name = file.name.toLowerCase()
  if (file.type.startsWith("image/")) return "photos"
  if (/догов|оферт|contract/.test(name)) return "contract"
  if (/презент|presentation|\.pptx?$|\.key$/.test(name)) return "presentation"
  if (/прайс|price|остат|\.xlsx?$|\.csv$/.test(name)) return "price_list"
  if (/каталог|catalog|lookbook/.test(name)) return "catalog"
  if (/инн|огрн|устав|выписк|карточк|реквизит|паспорт/.test(name)) return "documents"
  if (/\.pdf$/.test(name)) return "catalog"
  return "other"
}

/** Открытые стадии: на них у заявки должен быть следующий шаг. */
export const CLOSED_STATUSES: readonly ApplicationStatus[] = ["active", "rejected", "lost"]

export const toLocalInput = (iso: string | null | undefined): string => {
  if (!iso) return ""
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ""
  const pad = (n: number) => String(n).padStart(2, "0")
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

export const fromLocalInput = (value: string): string | null => {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date.toISOString()
}

export const errorText = (error: unknown): string => (error instanceof Error ? error.message : String(error))
